import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import Swal from 'sweetalert2';

import { HomeService } from '../../../../services/home.service';
import { users } from '../../../../interfaces/users';
import { ExcelExportService } from '../../../../services/excel-export.service';
import { UserService } from '../../../../services/user';
import { ItemsService } from '../../../../services/items.service';
import { WarehouseTypeService } from '../../../../services/warehouse-type';

@Component({
  selector: 'app-allusers',
  templateUrl: './allusers.component.html',
  styleUrl: './allusers.component.scss'
})
export class AllusersComponent implements OnInit {
  allData: users[] = [];
  allUsersRaw: users[] = [];
  allDataBackup: users[] = [];

  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);

  selectedUserType: string = '';
  filteredUserTypes: { value: string; label: string }[] = [];

  userTypes = [
    { value: '', label: 'Users' },
    { value: '1', label: 'admin' },
    { value: '2', label: 'Affiliation' },
    { value: '3', label: 'Page A' },
    { value: '4', label: 'B' },
    { value: '5', label: 'C' },
    { value: '6', label: 'subadmin' },
    { value: '7', label: 'employee' },
    { value: '8', label: 'deliveryhero' },
    { value: '9', label: 'client' },
    { value: '10', label: 'super admin' },
  ];

  displayedColumns: string[] = [
    'username',
    'first_name',
    'last_name',
    'phone',
    'type',
    'instagram_link',
    'tiktok_link',
    'facebook_link'
  ];

  imageURL = '/assets/Home.png';

  contentReady: boolean = true;
  noData: boolean = false;
  showSearch: boolean = false;
  searchValue: string = '';
  searchTimer: any;

  warehouseId?: any;
  allWareHouses: any[] = [];

  isAr: boolean = localStorage.getItem('language') === 'ar';

  // Pagination
  pageIndex: number = 0;
  currentPage: number = 1;
  pageSize: number = 10;
  totalUsers: number = 0;
  lastPage: number = 1;
  from: number = 0;
  to: number = 0;
  pageSizeOptions: number[] = [10, 25, 50, 100];

  constructor(
    private router: Router,
    private _homeService: HomeService,
    public warehouseTypeService: WarehouseTypeService,
    private _userService: UserService,
    private _ItemsService: ItemsService,
    private excelExportService: ExcelExportService,
  ) {}

  ngOnInit(): void {
    this.warehouseId = localStorage.getItem('posId');
    this.contentReady = true;

    this._userService.getAccountType().subscribe({
      next: (data) => {
        const userType = String(data['user_type']);

        if (
          (userType === '1' || userType === '10') &&
          !this.displayedColumns.includes('delete')
        ) {
          this.displayedColumns.push('delete');
        }

        this.setFilteredUserTypes();

        this._ItemsService.getWearHouses().subscribe({
          next: (res) => {
            this.allWareHouses = res?.warehouse || [];

            if (!this.warehouseId && this.allWareHouses.length > 0) {
              this.warehouseId = this.allWareHouses[0].id;
              localStorage.setItem('posId', String(this.warehouseId));
            }

            this.loadUsers(1);
          },
          error: () => {
            this.loadUsers(1);
          }
        });
      },
      error: (error) => {
        console.error(error);
        this.contentReady = false;
      },
    });
  }

  setFilteredUserTypes(): void {
    this.filteredUserTypes = this.userTypes.filter(type =>
      this.shouldShowUserType(type.value)
    );

    if (
      this.selectedUserType &&
      !this.filteredUserTypes.some(type => type.value === this.selectedUserType)
    ) {
      this.selectedUserType = '';
    }
  }

  shouldShowUserType(value: string): boolean {
    switch (value) {
      case '2':
      case '3':
      case '4':
      case '5':
      case '9':
        return this.warehouseTypeService.isCompany();

      case '8':
        return (
          this.warehouseTypeService.isCompany() ||
          this.warehouseTypeService.isShipping()
        );

      default:
        return true;
    }
  }

  loadUsers(page: number = 1): void {
    if (!this.warehouseId) {
      this.resetUsersTable();
      return;
    }

    this.contentReady = true;

    const search = this.searchValue.trim();
    const hasSearch = search.length > 0;

    const request$ = hasSearch
      ? this._homeService.searchUsersByWarehouse(
          this.warehouseId,
          page,
          this.pageSize,
          {
            search: search,
            type: this.selectedUserType
          }
        )
      : this._homeService.getUsersByWarehouse(
          this.warehouseId,
          page,
          this.pageSize,
          this.selectedUserType
        );

    request$.subscribe({
      next: (res) => {
        const paginator = res?.users;
        const usersList = paginator?.data || [];

        this.allUsersRaw = usersList;
        this.allData = usersList;
        this.allDataBackup = [...usersList];
        this.dataSource = new MatTableDataSource<any>(usersList);

        this.currentPage = Number(paginator?.current_page || page);
        this.pageIndex = this.currentPage - 1;
        this.pageSize = Number(paginator?.per_page || this.pageSize);
        this.totalUsers = Number(paginator?.total || 0);
        this.lastPage = Number(paginator?.last_page || 1);
        this.from = Number(paginator?.from || 0);
        this.to = Number(paginator?.to || 0);

        this.noData = usersList.length === 0;
        this.contentReady = false;
      },
      error: (err) => {
        console.error(err);
        this.contentReady = false;
        this.noData = true;
      }
    });
  }

  resetUsersTable(): void {
    this.allUsersRaw = [];
    this.allData = [];
    this.allDataBackup = [];
    this.dataSource = new MatTableDataSource<any>([]);

    this.totalUsers = 0;
    this.currentPage = 1;
    this.pageIndex = 0;
    this.lastPage = 1;
    this.from = 0;
    this.to = 0;

    this.noData = true;
    this.contentReady = false;
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    const nextPage = event.pageIndex + 1;
    this.loadUsers(nextPage);
  }

  applyWareHouse(id: any): void {
    this.warehouseId = id;
    localStorage.setItem('posId', String(id));

    this.searchValue = '';
    this.selectedUserType = '';
    this.pageIndex = 0;
    this.currentPage = 1;

    this.loadUsers(1);
  }

  filterByUserType(type: string): void {
    this.selectedUserType = type;

    this.pageIndex = 0;
    this.currentPage = 1;

    this.loadUsers(1);
  }

  applyFilter(event: Event): void {
    this.searchValue = (event.target as HTMLInputElement).value.trim();

    clearTimeout(this.searchTimer);

    this.searchTimer = setTimeout(() => {
      this.pageIndex = 0;
      this.currentPage = 1;
      this.loadUsers(1);
    }, 400);
  }

  clearSearch(input?: HTMLInputElement): void {
    this.searchValue = '';

    if (input) {
      input.value = '';
    }

    this.pageIndex = 0;
    this.currentPage = 1;

    this.loadUsers(1);
  }

  toggleSearch(): void {
    this.showSearch = !this.showSearch;

    if (!this.showSearch) {
      this.searchValue = '';
      this.pageIndex = 0;
      this.currentPage = 1;
      this.loadUsers(1);
    }
  }

  showAllExceptClients(): void {
    this.selectedUserType = '';
    this.pageIndex = 0;
    this.currentPage = 1;
    this.loadUsers(1);
  }

  getUserType(user: any): string {
    return String(user?.type ?? '');
  }

  go(url: string): void {
    if (!url) return;
    window.open(url, '_blank');
  }

  edituser(itemobj: any): void {
    this.router.navigate(['/Resigter'], { queryParams: itemobj });
  }

  deleteuser(id: string): void {
    Swal.fire({
      title: this.isAr ? 'متأكد؟؟' : 'Are You Sure?',
      showCancelButton: true,
      confirmButtonText: this.isAr ? 'اه' : 'YES',
      cancelButtonText: this.isAr ? 'لا' : 'no'
    }).then((result) => {
      if (result.isConfirmed) {
        this.contentReady = true;

        this._homeService.deleteUser(id).subscribe({
          next: () => {
            const reloadPage =
              this.allUsersRaw.length === 1 && this.currentPage > 1
                ? this.currentPage - 1
                : this.currentPage;

            this.loadUsers(reloadPage);
          },
          error: (err) => {
            console.log(err);
            this.contentReady = false;
          },
        });
      }
    });
  }

  exportToExcel(): void {
    this.excelExportService.exportToExcel('userstable', 'BlueBox Users', ['delete']);
  }

  showpassword(password: any): void {
    Swal.fire({
      title: this.isAr ? 'كلمة المرور' : 'User Password',
      text: `${password}`,
      icon: 'info',
      confirmButtonText: this.isAr ? 'تمام' : 'OK',
      confirmButtonColor: '#1E3A8A'
    });
  }
}